import React, { useEffect } from "react";
import useOrders from "./useOrders";
import { Link, useHistory } from "react-router-dom/cjs/react-router-dom.min";
import { API } from "../../../config";
import Loading from "../../../utils/LoadingIndicator/Loading";
import { useAuth } from "../../../utils/AuthContext";

function UserOrders({ onSetActiveTab, onSetOrder }) {
  const { isGettingUserOrders, userOrders, userOrdersError } = useOrders();
  const { isLoggedIn, user } = useAuth();
  const history = useHistory();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  if (!isLoggedIn) {
    return (
      <div className="flex flex-col gap-12 items-center justify-center w-full">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width={125}
          height={125}
          viewBox="0 0 24 24"
        >
          <path
            fill="currentColor"
            fillOpacity={0}
            stroke="#ff0000"
            strokeDasharray={64}
            strokeDashoffset={64}
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 2l-9 3.5v6.5c0 3.5 3.5 9 8 10c4.5 -1 8 -6.5 8 -10v-6.5l-8 -3.5Z"
          >
            <animate
              fill="freeze"
              attributeName="fill-opacity"
              begin="0.7s"
              dur="0.15s"
              values="0;0.3"
            ></animate>
            <animate
              fill="freeze"
              attributeName="stroke-dashoffset"
              dur="0.6s"
              values="64;0"
            ></animate>
          </path>
        </svg>
        <p className="text-5xl font-semibold text-red-500">Please Login</p>
        <Link to="/signin" className="text-2xl underline text-black">
          Login
        </Link>
      </div>
    );
  }

  if (isGettingUserOrders) {
    return <Loading />;
  }

  if (userOrdersError) {
    return (
      <div className="flex items-center justify-center min-h-[200px]">
        <h2 className="text-red-500 text-xl">{userOrdersError}</h2>
      </div>
    );
  }

  // Calculate total amount spent
  const totalAmount = userOrders.reduce((sum, order) => sum + order.amount, 0);

  const defaultAddress = user.addresses.find((address) => address.isDefault);
  return (
    <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4">
      <div className="bg-white shadow-lg rounded-lg w-full max-w-4xl p-6">
        {/* User Profile Section */}
        <div className="border-b pb-4 mb-4">
          <h1 className="text-2xl font-bold text-gray-800">User Profile</h1>
          <div className="mt-2 space-y-1">
            <p>
              <span className="font-semibold">Name:</span> {user?.name}
            </p>
            <p>
              <span className="font-semibold">Address:</span>{" "}
              {defaultAddress?.city}
            </p>
            <p>
              <span className="font-semibold">Phone Number:</span>{" "}
              {user?.phoneNo}
            </p>
            <p>
              <span className="font-semibold">Status:</span>{" "}
              {user?.isDeleted === 0 ? "Active" : "Deleted"}
            </p>
          </div>
        </div>

        {/* Purchase History Section */}
        <div>
          <h2 className="text-xl font-semibold text-gray-800 mb-4">
            Purchase History
          </h2>
          <div className="mb-4">
            <p>
              <span className="font-semibold">Total Orders:</span>{" "}
              {userOrders.length}
            </p>
            {Number.isNaN(Number(Math.floor(totalAmount))) ? (
              ""
            ) : (
              <p>
                <span className="font-semibold">Total Amount Spent: </span>
                {Math.floor(totalAmount)} EGP
              </p>
            )}
          </div>

          {/* Orders Table */}
          <div className="overflow-x-auto">
            <table className="min-w-full bg-white border">
              <thead>
                <tr className="bg-gray-200 text-gray-600 uppercase text-sm leading-normal">
                  <th className="py-3 px-6 text-left">Image</th>
                  <th className="py-3 px-6 text-left">Order ID</th>
                  <th className="py-3 px-6 text-left">Date</th>
                  <th className="py-3 px-6 text-left">Status</th>
                  <th className="py-3 px-6 text-right">Amount</th>
                  <th className="py-3 px-6 text-left">Items</th>
                </tr>
              </thead>
              <tbody className="text-gray-600 text-sm">
                {userOrders.map((order) => {
                  return (
                    <tr
                      onClick={() => {
                        onSetActiveTab("details");
                        onSetOrder(order);
                      }}
                      key={order._id}
                      className="border-b hover:bg-gray-100 cursor-pointer"
                    >
                      <td className="py-3 px-6">
                        <div>
                          <img
                            src={`${API}/products/photo/${order.products[0]._id}`}
                          />
                        </div>
                      </td>
                      <td className="py-3 px-6">{order._id}</td>
                      <td className="py-3 px-6">
                        {new Date(order.createdAt).toLocaleDateString()}
                      </td>
                      <td className="py-3 px-6">
                        <span
                          className={`px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full 
                        ${
                          order.status === "Processing"
                            ? "bg-yellow-100 text-yellow-800"
                            : order.status === "Shipped"
                            ? "bg-blue-100 text-blue-800"
                            : order.status === "Delivered"
                            ? "bg-green-100 text-green-800"
                            : order.status === "Cancelled"
                            ? "bg-red-100 text-red-800"
                            : "bg-gray-100 text-gray-800"
                        }`}
                        >
                          {order.status}
                        </span>
                      </td>
                      <td className="py-3 px-6 text-right">
                        {Math.floor(order.amount)} EGP
                      </td>
                      <td className="py-3 px-6">
                        <div className="max-h-20 overflow-y-auto">
                          {order.products?.map((product, index) => (
                            <div key={index} className="mb-1">
                              {product.name} (Qty: {product.quantity})
                            </div>
                          ))}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

export default UserOrders;
